import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const PracticeChecklist = ({ checklistData, onUpdateChecklist }) => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const handleItemCheck = (sectionId, itemId, checked) => {
    onUpdateChecklist(sectionId, itemId, checked);
  };

  const getSectionProgress = (section) => {
    const completedItems = section?.items?.filter(item => item?.completed)?.length;
    return Math.round((completedItems / section?.items?.length) * 100);
  };

  const getSectionIcon = (type) => {
    switch (type) {
      case 'food-safety':
        return 'Shield';
      case 'cold-chain':
        return 'Thermometer';
      case 'quality':
        return 'CheckCircle2';
      case 'operations':
        return 'Settings';
      default:
        return 'List';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground">
            Lista de Verificación de Prácticas
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Cumplimiento de estándares operativos
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="ClipboardCheck" size={20} color="var(--color-primary)" />
        </div>
      </div>
      <div className="space-y-4">
        {checklistData?.sections?.map((section) => {
          const progress = getSectionProgress(section);
          const isExpanded = expandedSections?.[section?.id];

          return (
            <div key={section?.id} className="border border-border rounded-lg">
              <button
                onClick={() => toggleSection(section?.id)}
                className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-smooth rounded-t-lg"
              >
                <div className="flex items-center space-x-3">
                  <Icon 
                    name={getSectionIcon(section?.type)} 
                    size={20} 
                    color="var(--color-primary)" 
                  />
                  <div className="text-left">
                    <h3 className="font-medium text-foreground">{section?.title}</h3>
                    <p className="text-sm text-muted-foreground">{section?.description}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <div className="text-sm font-medium text-foreground">{progress}%</div>
                    <div className="w-16 bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                  <Icon 
                    name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
                    size={20} 
                    color="var(--color-muted-foreground)" 
                  />
                </div>
              </button>
              {isExpanded && (
                <div className="border-t border-border p-4 bg-muted/20">
                  <div className="space-y-3">
                    {section?.items?.map((item) => (
                      <div key={item?.id} className="flex items-start space-x-3">
                        <Checkbox
                          checked={item?.completed}
                          onChange={(e) => handleItemCheck(section?.id, item?.id, e?.target?.checked)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <label className="text-sm font-medium text-foreground cursor-pointer">
                            {item?.title}
                          </label>
                          {item?.description && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {item?.description}
                            </p>
                          )}
                          {item?.required && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-error/10 text-error mt-1">
                              <Icon name="AlertCircle" size={12} className="mr-1" />
                              Obligatorio
                            </span>
                          )}
                        </div>
                        {item?.completed && (
                          <Icon name="Check" size={16} color="var(--color-success)" />
                        )}
                      </div>
                    ))}
                  </div>

                  {section?.notes && (
                    <div className="mt-4 p-3 bg-accent/10 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <Icon name="Info" size={16} color="var(--color-accent)" className="mt-0.5" />
                        <div>
                          <h4 className="text-sm font-medium text-foreground mb-1">
                            Notas Importantes
                          </h4>
                          <p className="text-xs text-muted-foreground">
                            {section?.notes}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-6 border-t border-border">
        <Button variant="default" className="flex-1">
          <Icon name="Save" size={16} className="mr-2" />
          Guardar Progreso
        </Button>
        <Button variant="outline" className="flex-1">
          <Icon name="Download" size={16} className="mr-2" />
          Exportar Lista
        </Button>
      </div>
    </div>
  );
};

export default PracticeChecklist;